import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Printer, RefreshCw, Building2, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend,
} from "recharts";

const csvEscape = (v) => {
  let s = v == null ? "" : String(v);
  if (/^[=+\-@]/.test(s)) s = "'" + s;
  if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function AdminEmployerUtilization() {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [dateFrom, setDateFrom] = useState(daysAgoIso(89));
  const [dateTo, setDateTo] = useState(todayIso());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/organizations").then((r) => {
      const active = (r.data || []).filter((o) => o.status === "active");
      setOrgs(active);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const load = async () => {
    if (!orgId) { toast.error("Select an organization first."); return; }
    setLoading(true);
    try {
      const r = await api.get("/admin/employer-utilization", {
        params: { organization_id: orgId, date_from: dateFrom, date_to: dateTo },
      });
      setReport(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setReport(null);
    } finally { setLoading(false); }
  };

  const setPreset = (days) => {
    const to = todayIso();
    const from = days === "ytd" ? `${new Date().getFullYear()}-01-01` : daysAgoIso(days - 1);
    setDateFrom(from); setDateTo(to);
  };

  const exportCsv = () => {
    if (!report || report.suppressed) { toast.error("Nothing to export."); return; }
    const orgName = report.organization?.name || "";
    const lines = [];
    lines.push(["CCCHAPS Utilization Report"].map(csvEscape).join(","));
    lines.push(["Organization", orgName].map(csvEscape).join(","));
    lines.push(["Period From", report.period.from].map(csvEscape).join(","));
    lines.push(["Period To", report.period.to].map(csvEscape).join(","));
    lines.push(["Generated", new Date().toISOString()].map(csvEscape).join(","));
    lines.push("");
    lines.push("Summary Metrics");
    lines.push(["Total Contacts", report.metrics.total_contacts].map(csvEscape).join(","));
    lines.push(["Unique Employees Supported", report.metrics.unique_employees].map(csvEscape).join(","));
    lines.push(["Requests Received", report.metrics.requests_received].map(csvEscape).join(","));
    lines.push(["Average Contacts per Employee", report.metrics.avg_contacts_per_employee].map(csvEscape).join(","));
    lines.push("");
    lines.push("How Support Was Delivered");
    lines.push(["Contact Type", "Count"].join(","));
    report.contact_type_breakdown.forEach((r) => lines.push([r.label, r.value].map(csvEscape).join(",")));
    lines.push("");
    lines.push("Areas of Support");
    lines.push(["Area", "Count"].join(","));
    report.areas_of_support_breakdown.forEach((r) => lines.push([r.label, r.value].map(csvEscape).join(",")));
    lines.push("");
    lines.push(`Trend (${report.period.bucketing})`);
    lines.push(["Period", "Count"].join(","));
    report.trend.forEach((r) => lines.push([r.period, r.value].map(csvEscape).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    const safeSlug = (report.organization?.slug || "organization").replace(/[^a-z0-9-]+/gi, "");
    a.download = `CCCHAPS_Utilization_${safeSlug}_${todayIso()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const orgEmpty = orgs.length === 0;

  return (
    <div data-testid="admin-employer-utilization">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: white !important; }
          .print-avoid-break { page-break-inside: avoid; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Print header */}
      {report && !report.suppressed && (
        <div className="print-only mb-4">
          <h1 className="font-heading text-2xl">CCCHAPS Utilization Report</h1>
          <p className="text-sm text-slate-700 mt-1">{report.organization.name} · {report.period.from} to {report.period.to}</p>
          <p className="text-[11px] text-slate-500 mt-1">Aggregate summary — no individual employee data included. Generated {new Date().toLocaleString()}.</p>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap no-print">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900">Employer Utilization</h2>
          <p className="text-sm text-slate-500">Aggregate-only reporting scoped by organization. Safe for sharing with client leadership.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={!orgId || loading} data-testid="eu-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:border-[#4A6FA5] disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2.2} /> Refresh</button>
          <button onClick={exportCsv} disabled={!report || report.suppressed} data-testid="eu-csv" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] disabled:opacity-50 text-white text-sm font-semibold uppercase tracking-wider"><Download className="w-4 h-4" strokeWidth={2.2} /> Export CSV</button>
          <button onClick={() => window.print()} disabled={!report || report.suppressed} data-testid="eu-print" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:border-[#4A6FA5] disabled:opacity-50"><Printer className="w-4 h-4" strokeWidth={2.2} /> Print / Save PDF</button>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-2 text-xs text-[#3B597B] no-print"><ShieldCheck className="w-3.5 h-3.5 mt-0.5" strokeWidth={2.2} /><p>Aggregate-only. No employee names, contact details, notes, reasons, or individual histories are ever included. Suppressed when fewer than 5 unique employees; rare categories folded into "Other (small counts)".</p></div>

      {orgEmpty ? (
        <div data-testid="eu-empty-orgs" className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Building2 className="w-8 h-8 text-slate-400 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-slate-700 font-medium">No organizations are available for reporting yet.</p>
          <p className="text-xs text-slate-500 mt-1">Add an organization in <a href="/care-portal/admin/organizations" className="text-[#4A6FA5] underline">Admin → Organizations</a> to begin generating utilization reports.</p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div data-testid="eu-filters" className="mb-6 rounded-xl border border-slate-200 bg-white p-4 grid grid-cols-2 md:grid-cols-4 gap-3 no-print">
            <label className="block text-xs col-span-2">
              <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Organization</span>
              <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setReport(null); }} data-testid="eu-org-select" className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm bg-white">
                <option value="">Select an organization…</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
            <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">From</span><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="eu-date-from" className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm" /></label>
            <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">To</span><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="eu-date-to" className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm" /></label>
            <div className="col-span-2 md:col-span-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-1.5">
                {[["30d", 30], ["90d", 90], ["YTD", "ytd"]].map(([lbl, v]) => (
                  <button key={lbl} type="button" onClick={() => setPreset(v)} data-testid={`eu-preset-${lbl.toLowerCase()}`} className="px-2.5 py-1 rounded border border-slate-300 text-xs text-slate-600 hover:border-[#4A6FA5]">Last {lbl}</button>
                ))}
              </div>
              <button onClick={load} disabled={!orgId || loading} data-testid="eu-apply" className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white text-xs font-semibold uppercase tracking-wider">Generate Report</button>
            </div>
          </div>

          {/* Suppressed panel */}
          {report && report.suppressed && (
            <div data-testid="eu-suppressed" className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center">
              <ShieldCheck className="w-8 h-8 text-slate-400 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-slate-800 font-medium">Not enough activity in this period to share while protecting employee privacy.</p>
              <p className="text-xs text-slate-500 mt-1">This organization has fewer than {report.min_unique_employees} unique employees with recorded activities in the selected period. Try a longer date range.</p>
            </div>
          )}

          {/* Report body */}
          {report && !report.suppressed && (
            <>
              <p className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2 no-print">{report.organization.name} · {report.period.from} → {report.period.to}</p>
              <div data-testid="eu-tiles" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 print-avoid-break">
                <Tile label="Total Contacts" value={report.metrics.total_contacts} testId="eu-tile-total" />
                <Tile label="Unique Employees Supported" value={report.metrics.unique_employees} testId="eu-tile-employees" />
                <Tile label="Requests Received" value={report.metrics.requests_received} testId="eu-tile-requests" />
                <Tile label="Avg Contacts / Employee" value={Number(report.metrics.avg_contacts_per_employee).toFixed(1)} testId="eu-tile-avg" />
              </div>

              <div className="grid lg:grid-cols-2 gap-4 mb-6 print-avoid-break">
                <ChartCard title="How support was delivered" testId="eu-chart-contact-type" data={report.contact_type_breakdown} color="#4A6FA5" />
                <ChartCard title="Areas of support" testId="eu-chart-areas" data={report.areas_of_support_breakdown} color="#7A5FB0" />
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 print-avoid-break">
                <p className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-3">Trend over time ({report.period.bucketing})</p>
                <div className="h-[260px]" data-testid="eu-chart-trend">
                  {report.trend.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-xs text-slate-400" data-testid="eu-chart-trend-empty">No contacts in the selected period.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={report.trend} margin={{ top: 5, right: 8, left: 0, bottom: 20 }}>
                        <CartesianGrid stroke="#EDF1F6" strokeDasharray="3 3" />
                        <XAxis dataKey="period" tick={{ fill: "#64748b", fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 11 }} />
                        <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="value" name="Contacts" stroke="#4A6FA5" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <p className="mt-4 text-[10px] text-slate-400 no-print">Rare categories (fewer than {report.privacy.min_bucket_count} contacts) are folded into "{report.privacy.other_label}" to prevent individual identification. Time buckets are aggregate counts only.</p>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Tile({ label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">{label}</p>
      <div className="mt-1 font-heading text-3xl text-slate-900 tracking-tight">{value ?? 0}</div>
    </div>
  );
}

function ChartCard({ title, testId, data, color }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-3">{title}</p>
      <div className="h-[260px]" data-testid={testId}>
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-slate-400" data-testid={`${testId}-empty`}>No data recorded for this period.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 40 }}>
              <CartesianGrid stroke="#EDF1F6" strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
              <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
              <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
